import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Users, X } from "lucide-react";

/**
 * Soft empty-state CTA on the Weeks page:
 * shown when the user has completed ≥ 3 weeks AND doesn't lead any group yet.
 * Dismissible client-side (localStorage flag).
 */
export default function LeadAGroupCTA({ completedCount, user }) {
  const [groups, setGroups] = useState([]);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("atc_leadcta_dismissed") === "1");

  useEffect(() => {
    // Only fetch if we might actually show — user isn't already a leader.
    if (user?.is_leader) return;
    api.get("/groups").then((r) => setGroups(r.data)).catch(() => {});
  }, [user?.is_leader]);

  if (!user || user.is_leader || dismissed) return null;
  if (completedCount < 3) return null;
  // If they already lead any group locally (safety), skip.
  if (groups.some((g) => g.is_leader)) return null;

  const hide = () => { setDismissed(true); localStorage.setItem("atc_leadcta_dismissed", "1"); };

  return (
    <section className="relative border border-dashed border-primary/40 bg-primary/[0.03] p-6 md:p-8" data-testid="lead-a-group-cta">
      <button onClick={hide} data-testid="lead-cta-dismiss"
        className="absolute top-3 right-3 text-muted-foreground hover:text-foreground" aria-label="Dismiss">
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-center gap-3 text-primary">
        <Users className="w-4 h-4" />
        <p className="text-xs uppercase tracking-[0.2em]">You're three weeks in</p>
      </div>
      <h2 className="font-serif text-3xl mt-3 leading-tight">
        Walk this again — <em>with someone.</em>
      </h2>
      <p className="text-muted-foreground mt-3 max-w-xl">
        The handbook was never meant to end with you. Start a group, share a code,
        and invite one person you're already thinking about. You'll grow twice.
      </p>
      <div className="mt-6">
        <Link to="/app/groups" data-testid="lead-cta-start" className="inline-block">
          <span className="rounded-full bg-primary text-primary-foreground px-6 py-2 text-sm hover:opacity-90">Start a group →</span>
        </Link>
      </div>
    </section>
  );
}
